import { __decorate } from "tslib";
import { Component, Prop, Mixins } from 'vue-property-decorator';
import VueRecaptcha from 'vue-recaptcha';
import ViewportSizeMixin from '@/helpers/mixins/ViewportSizeMixin';
import BaseTextField from '@/components/v2/BaseTextField.vue';
import BaseDatePicker from '@/components/datepicker/Index.vue';
import { email, required, sameAs, minLength } from 'vuelidate/lib/validators';
import ActionButton from '@/components/action_button/Index.vue';
import LoginLinkMixin from '@/helpers/vue_mixins/LoginLinkMixin';
import FormValidationsMixin from '@/helpers/vue_mixins/v2/FormValidationsMixin';
import AdminInvitationSignUpEmailMutation from '@/plugins/graphql/mutations/v2/auth/admin-invitation-signup-email.gql';
import AdminInvitationSignUpFacebookMutation from '@/plugins/graphql/mutations/v2/auth/admin-invitation-signup-facebook.gql';
import AuthMixin from '@/helpers/vue_mixins/v2/AuthMixin';
import { resetStore } from '@/plugins/vue-apollo';
import { FacebookLoginProvider } from '@/helpers/entities/classes/providers/FacebookLoginProvider';
import RecaptchaMixin from '@/helpers/mixins/RecaptchaMixin';
import AesMixin from '@/helpers/vue_mixins/v2/AesMixin';
import UserPilotMixin from '@/helpers/vue_mixins/v2/UserpilotMixin';
import me from '@/plugins/graphql/queries/me.gql';
import { PASSWORD_REGEXP } from '../helpers/constants/app';
// Initialize class FacebookLoginProvider
const Provider = new FacebookLoginProvider();
// TODO: find a way to prevent octal literal expression in joining
// validation of 3 consecutive chars
const invalidValue = (value) => PASSWORD_REGEXP.test(value) && !/(.)\1\1/.test(value);
let AdminInvitationSignUp = class AdminInvitationSignUp extends Mixins(Mixins(ViewportSizeMixin, FormValidationsMixin, AuthMixin, LoginLinkMixin, RecaptchaMixin), AesMixin, UserPilotMixin) {
    constructor() {
        super(...arguments);
        this.form = {
            firstName: null,
            lastName: null,
            email: null,
            password: null,
            confirmPassword: null,
            birthdate: null,
        };
    }
    // Lifecycle hooks
    created() {
        // Get all form details from encrypted URL parameters
        this.getDetailsFromUrlParameters();
    }
    // Get encrypted user details from URL parameters
    getDetailsFromUrlParameters() {
        const urlParams = this.$route.query;
        const dataParam = urlParams.data;
        if (urlParams.data) {
            try {
                // Ensure we're working with a string, not an array
                const dataString = Array.isArray(dataParam) ? dataParam[0] : dataParam;
                // Use AesMixin's decrypt method
                const decrypted = this.decrypt(decodeURIComponent(dataString));
                const userData = JSON.parse(decrypted);
                // Set form values
                if (userData.firstName)
                    this.form.firstName = userData.firstName;
                if (userData.lastName)
                    this.form.lastName = userData.lastName;
                if (userData.email)
                    this.form.email = userData.email;
            }
            catch (error) {
                console.error('Failed to decrypt URL parameters:', error);
                // Show error notification to user
                this.errorNotification('Failed to load your details. Please try again or contact support.');
            }
        }
        else {
            console.warn('No encrypted data found in URL parameters');
        }
    }
    get passwordCriteria() {
        return [
            `Alphanumeric and at least 8 characters with one special character.`,
            `The password cannot have more than two consecutive identical character (example: 111).`,
            `The password is case-sensitive.`,
            `The characters ' " & and spaces are not allowed.`,
            `The password cannot be the same as your merchant
       login name and should not contain any part of your company name or username.`,
        ];
    }
    async onSignUpEmail() {
        this.$v.$touch();
        if (!this.$v.$invalid) {
            this.requestOnFlight();
            await this.signUpEmail();
        }
    }
    onSignUpFacebook() {
        // Default basic permission ask in admin invitation
        Provider.signIn(true).then((response) => {
            if (response.status === 'connected') {
                /**
                 * Trigger Facebook Login Mutation if User is Successfully
                 *  Authenticated.
                 * https://developers.facebook.com/docs/facebook-login/web
                 */
                const token = response.authResponse.accessToken;
                this.signUpFacebook(token);
            }
        });
    }
    async signUpEmail() {
        if (this.isRobot) {
            this.recaptchaErrorNotifier();
            return;
        }
        return this.$apollo
            .mutate({
            mutation: AdminInvitationSignUpEmailMutation,
            variables: {
                input: {
                    firstName: this.encrypt(this.form.firstName),
                    lastName: this.encrypt(this.form.lastName),
                    email: this.encrypt(this.form.email),
                    password: this.encrypt(this.form.password),
                    invitationToken: this.encrypt(this.invitationToken),
                    // captchaSignature: this.encrypt(this.jwtCaptcha),
                    birthdate: this.encrypt(this.form.birthdate),
                },
            },
        })
            .then((result) => {
            const data = result.data.signUpAdminInvitationEmail;
            this.userpilotIdentify(data.id, {
                name: `${data.firstName} ${data.lastName}`,
                email: data.email,
                role: data.role,
            });
            this.userpilotTrack('New User Signup');
            this.$router.push(`/login/${data.role}`);
            this.successNotification(data.message);
            // this.resetRecaptcha();
            this.requestDone();
        })
            .catch((e) => {
            this.recaptchaErrorNotifier(e.message);
        });
    }
    signUpFacebook(token) {
        return this.$apollo
            .mutate({
            mutation: AdminInvitationSignUpFacebookMutation,
            variables: {
                input: {
                    token: this.encrypt(token),
                    invitationToken: this.encrypt(this.invitationToken),
                },
            },
        })
            .then((result) => {
            const data = result.data.signUpAdminInvitationFacebook;
            const plan = this.$router.currentRoute.query.plan_type;
            const code = this.$router.currentRoute.query.affiliate_code;
            this.authenticate(data.jwt, data.role);
            resetStore().then(() => {
                this.successNotification('Signed In Successfully');
                this.$store.commit('navigation_drawer/toggleDrawer');
                this.$apollo
                    .query({
                    query: me,
                })
                    .then((meQuery) => {
                    const profile = meQuery.data.me;
                    this.userpilotIdentify(profile.id, {
                        name: `${profile.firstName} ${profile.lastName}`,
                        email: profile.email,
                        role: profile.role,
                    });
                    this.userpilotTrack('User Logged In', {
                        name: `${profile.firstName} ${profile.lastName}`,
                    });
                    if (data.newUser) {
                        this.userpilotTrack('New User Signup');
                    }
                });
                this.$router.push(this.buildLoginLink(plan, code, true));
            });
            this.resetClient(data.jwt);
            this.requestDone();
        })
            .catch((e) => {
            this.errorNotification(e.message);
            this.requestDone();
        });
    }
};
__decorate([
    Prop({ required: true })
], AdminInvitationSignUp.prototype, "invitationToken", void 0);
AdminInvitationSignUp = __decorate([
    Component({
        components: {
            VueRecaptcha,
            BaseTextField,
            BaseDatePicker,
            ActionButton,
        },
        mounted() {
            // Call #initialize function to append facebook sdk into DOM head
            Provider.initialize();
        },
        beforeRouteEnter(to, from, next) {
            next((vm) => {
                vm.$store.state.displayHomeNav = false;
                return true;
            });
        },
        validations: {
            form: {
                firstName: {
                    required,
                },
                lastName: {
                    required,
                },
                birthdate: { required },
                email: {
                    email,
                    required,
                },
                password: {
                    required,
                    minLength: minLength(8),
                    invalidValue,
                },
                confirmPassword: {
                    required,
                    sameAsPassword: sameAs('password'),
                },
            },
        },
    })
], AdminInvitationSignUp);
export default AdminInvitationSignUp;
